/**
 * The shared **card-face renderer** (trading card game, PRD #493 Slice 3): builds
 * one DOM card face - a rarity-coloured frame (bronze / silver / gold) around a
 * card's cost, name, kind, and stats - from a pure {@link CardDef}. The Card Binder's
 * Collection and Decks grids compose it, and the later battle gump (Slice 5) reuses
 * the same renderer, so a card looks identical everywhere.
 *
 * Monster cards crop a front-facing idle frame directly from the same baked body atlas
 * used by the Pixi world.
 * This keeps card art registry-driven without maintaining a parallel illustration set.
 * Spell and item cards crop native UO effect and item art from the baked effect/static
 * atlases, with a magic sigil retained only as a missing-art fallback.
 * Like every other piece of britbank chrome it is `textContent`-only (never
 * `innerHTML`), so a card name can never inject markup.
 *
 * Pure DOM: it reads a {@link CardFaceModel} (built by {@link cardFaceModel} from the
 * sim's card definitions) and returns an element. No game logic, no world state - the
 * gump holds neither (ADR-0268: gumps render state and send intents only).
 */
import {
  actionCardDescription,
  cardKeywordText,
  scriptedEffectText,
  type CardDef,
  type CardKind,
  type CardRarity,
  type SpellSchool,
} from './sim/index';
import {
  cardActionArt,
  cardActionBackdropArt,
  cardSpriteArt,
  paintHuedCardSprite,
  type CardSpriteArt,
} from './cardArt';

/** Converter-pixel fit box used only to normalize creature crops into relative widths. */
const SPRITE_REFERENCE_WIDTH = 168;
const SPRITE_REFERENCE_HEIGHT = 94;
const MAX_SPRITE_SCALE = 2.2;
const MAX_ACTION_SPRITE_SCALE = 6;
const FIREBALL_SPRITE_SCALE = 2.55;
const ITEM_ART_SIZE = 0.8;
const EXPLOSION_BACKDROP_SIZE = 1.3;

/** Everything the face draws, distilled from a {@link CardDef} (plus an optional owned count). */
export interface CardFaceModel {
  cardId: string;
  name: string;
  rarity: CardRarity;
  cost: number;
  kind: CardKind;
  /** Monster attack (absent for spells and items). */
  attack?: number;
  /** Monster health (absent for spells and items). */
  health?: number;
  /** UO body id used to draw a monster's baked idle sprite. */
  body?: number;
  /** Canonical encoded UO hue applied to the monster art. */
  hue?: number;
  /** A short descriptive line: a monster's keywords, or an action card's effect. */
  detail?: string;
  slayerGroup?: string;
  school?: SpellSchool;
  circle?: number;
  /** Copies owned, shown as a labelled counter when supplied by a collection view. */
  count?: number;
}

/** Build the face model for a concrete card definition (optionally with an owned count). */
export function cardFaceModel(def: CardDef, count?: number): CardFaceModel {
  if (def.kind === 'monster') {
    return {
      cardId: def.cardId,
      name: def.name,
      rarity: def.rarity,
      cost: def.cost,
      kind: 'monster',
      attack: def.attack,
      health: def.health,
      body: def.body,
      hue: def.hue,
      detail: [
        def.keywords.map((keyword) => cardKeywordText(keyword, def.abilities.heal)).join(' '),
        def.abilities.onPlay ? `On play: ${scriptedEffectText(def.abilities.onPlay)}` : '',
        def.abilities.onDeath ? `On death: ${scriptedEffectText(def.abilities.onDeath)}` : '',
        def.abilities.endOfTurn ? `End of turn: ${scriptedEffectText(def.abilities.endOfTurn)}` : '',
      ].filter(Boolean).join(' · ') || undefined,
      slayerGroup: def.slayerGroup ? capitalize(def.slayerGroup) : undefined,
      count,
    };
  }
  return {
    cardId: def.cardId,
    name: def.name,
    rarity: def.rarity,
    cost: def.cost,
    kind: def.kind,
    detail: actionCardDescription(def),
    ...(def.kind === 'spell' ? { school: def.school, circle: def.circle } : {}),
    count,
  };
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/** CSS background-position percentage that preserves one cropped atlas coordinate. */
function cropPosition(offset: number, atlasSize: number, cropSize: number): number {
  const travel = atlasSize - cropSize;
  return travel <= 0 ? 0 : (offset / travel) * 100;
}

/** Wrap an orb label so its optical offset is independent of the positioned square. */
function orbValue(doc: Document, value: number): HTMLElement {
  const label = doc.createElement('span');
  label.className = 'card-orb-value';
  label.textContent = String(value);
  return label;
}

function appendAtlasCrop(
  doc: Document,
  art: HTMLElement,
  sprite: CardSpriteArt,
  scale: number,
  extraClass?: string,
  zIndex?: number,
): HTMLElement {
  const crop = doc.createElement('span');
  crop.className = `card-art-sprite${extraClass ? ` ${extraClass}` : ''}`;
  crop.style.width = `${(sprite.width * scale / SPRITE_REFERENCE_WIDTH) * 100}%`;
  crop.style.aspectRatio = `${sprite.width} / ${sprite.height}`;
  crop.style.backgroundImage = `url("${sprite.atlasUrl}")`;
  crop.style.backgroundPosition = `${cropPosition(sprite.x, sprite.atlasWidth, sprite.width)}% ${cropPosition(sprite.y, sprite.atlasHeight, sprite.height)}%`;
  crop.style.backgroundSize = `${(sprite.atlasWidth / sprite.width) * 100}% ${(sprite.atlasHeight / sprite.height) * 100}%`;
  crop.style.gridArea = '1 / 1';
  crop.style.placeSelf = 'center';
  if (zIndex !== undefined) crop.style.zIndex = String(zIndex);
  art.appendChild(crop);
  return crop;
}

/**
 * Render one card face element. The rarity drives the frame colour via a
 * `rarity-<rarity>` class (the CSS in `index.html` maps all four rarities to their
 * dedicated frames); the `data-card-id`/`data-rarity` attributes let the gump find
 * and click a specific card without threading the model back through the DOM.
 */
export function renderCardFace(doc: Document, model: CardFaceModel): HTMLElement {
  const card = doc.createElement('div');
  card.className = `card-face rarity-${model.rarity}`;
  card.dataset.cardId = model.cardId;
  card.dataset.rarity = model.rarity;

  const cost = doc.createElement('span');
  cost.className = 'card-cost';
  cost.appendChild(orbValue(doc, model.cost));
  cost.setAttribute('aria-label', `${model.cost} mana`);

  const name = doc.createElement('span');
  name.className = 'card-name';
  name.textContent = model.name;

  const art = doc.createElement('span');
  art.className = 'card-art';
  art.setAttribute('aria-hidden', 'true');

  if (model.kind === 'monster' && model.body !== undefined) {
    const sprite = cardSpriteArt(model.body);
    if (sprite) {
      const scale = Math.min(
        MAX_SPRITE_SCALE,
        SPRITE_REFERENCE_HEIGHT / sprite.height,
        SPRITE_REFERENCE_WIDTH / sprite.width,
      );
      const crop = model.hue
        ? doc.createElement('canvas')
        : doc.createElement('span');
      crop.className = 'card-art-sprite';
      crop.style.width = `${(sprite.width * scale / SPRITE_REFERENCE_WIDTH) * 100}%`;
      crop.style.aspectRatio = `${sprite.width} / ${sprite.height}`;
      crop.style.backgroundImage = `url("${sprite.atlasUrl}")`;
      crop.style.backgroundPosition = `${cropPosition(sprite.x, sprite.atlasWidth, sprite.width)}% ${cropPosition(sprite.y, sprite.atlasHeight, sprite.height)}%`;
      crop.style.backgroundSize = `${(sprite.atlasWidth / sprite.width) * 100}% ${(sprite.atlasHeight / sprite.height) * 100}%`;
      if (crop.tagName === 'CANVAS') {
        const canvas = crop as HTMLCanvasElement;
        canvas.width = sprite.width;
        canvas.height = sprite.height;
        canvas.dataset.hue = String(model.hue);
        void paintHuedCardSprite(canvas, sprite, model.hue!);
      }
      art.appendChild(crop);
    } else {
      art.classList.add('card-art-missing');
      const missing = doc.createElement('span');
      missing.className = 'card-art-missing-mark';
      missing.textContent = '?';
      art.appendChild(missing);
    }
  } else if (model.kind !== 'monster') {
    const sprite = cardActionArt(model.cardId);
    if (sprite) {
      const backdrop = cardActionBackdropArt(model.cardId);
      if (backdrop) {
        const backdropScale = Math.min(
          MAX_ACTION_SPRITE_SCALE,
          SPRITE_REFERENCE_HEIGHT / backdrop.height,
          SPRITE_REFERENCE_WIDTH / backdrop.width,
        ) * EXPLOSION_BACKDROP_SIZE;
        appendAtlasCrop(doc, art, backdrop, backdropScale, 'card-art-backdrop', 0);
      }
      const size = model.cardId === 'fireball' ? 1 : ITEM_ART_SIZE;
      const scale = Math.min(
        model.cardId === 'fireball' ? FIREBALL_SPRITE_SCALE : MAX_ACTION_SPRITE_SCALE,
        SPRITE_REFERENCE_HEIGHT / sprite.height,
        SPRITE_REFERENCE_WIDTH / sprite.width,
      ) * size;
      appendAtlasCrop(doc, art, sprite, scale, 'card-art-action', 1);
    } else {
      const sigil = doc.createElement('span');
      sigil.className = 'card-art-sigil';
      sigil.textContent = '✦';
      art.appendChild(sigil);
    }
  } else {
    const sigil = doc.createElement('span');
    sigil.className = 'card-art-sigil';
    sigil.textContent = '✦';
    art.appendChild(sigil);
  }

  const kind = doc.createElement('span');
  kind.className = 'card-kind';
  kind.textContent = model.kind === 'monster'
    ? `${model.slayerGroup ? `${model.slayerGroup} ` : ''}Monster`
    : model.kind === 'item'
      ? 'Item'
      : `${model.circle ? `Circle ${model.circle} ` : ''}${model.school ? `${capitalize(model.school)} ` : ''}Spell`;

  card.append(cost, art, name, kind);

  if (model.detail) {
    const detail = doc.createElement('span');
    detail.className = 'card-detail';
    detail.textContent = model.detail;
    card.appendChild(detail);
  }

  if (model.kind === 'monster') {
    const stats = doc.createElement('span');
    stats.className = 'card-stats';
    stats.setAttribute('aria-label', `${model.attack ?? 0} attack, ${model.health ?? 0} health`);

    const attack = doc.createElement('span');
    attack.className = 'card-stat card-attack';
    attack.appendChild(orbValue(doc, model.attack ?? 0));
    attack.setAttribute('aria-hidden', 'true');

    const health = doc.createElement('span');
    health.className = 'card-stat card-health';
    health.appendChild(orbValue(doc, model.health ?? 0));
    health.setAttribute('aria-hidden', 'true');

    stats.append(attack, health);
    card.appendChild(stats);
  }

  if (model.count !== undefined) {
    const count = doc.createElement('span');
    count.className = 'card-count';
    count.setAttribute('role', 'group');
    count.setAttribute('aria-label', `${model.count} owned`);

    const label = doc.createElement('span');
    label.className = 'card-count-label';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = 'Owned';

    const value = doc.createElement('span');
    value.className = 'card-count-value';
    value.setAttribute('aria-hidden', 'true');
    value.textContent = String(model.count);

    count.append(label, value);
    card.appendChild(count);
  }

  return card;
}
